import api from './api'

export interface GeoItem {
  id: number
  key: string
  label: string
  meta?: Record<string, unknown> | null
}

const CACHE_TTL = 5 * 60 * 1000 // 5 minutos
const cache = new Map<string, { data: unknown; expires: number }>()
const inflight = new Map<string, Promise<unknown>>()

function cacheKey(url: string, params?: Record<string, string>): string {
  if (!params) return url
  return `${url}?${Object.entries(params)
    .sort()
    .map(([k, v]) => `${k}=${v}`)
    .join('&')}`
}

function getCached<T>(key: string): T | undefined {
  const entry = cache.get(key)
  if (!entry) return undefined
  if (Date.now() > entry.expires) {
    cache.delete(key)
    return undefined
  }
  return entry.data as T
}

function setCached(key: string, data: unknown): void {
  cache.set(key, { data, expires: Date.now() + CACHE_TTL })
}

async function fetchOnce<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const cached = getCached<T>(key)
  if (cached) return cached

  const existing = inflight.get(key)
  if (existing) return existing as Promise<T>

  const promise = fetcher()
    .then(data => {
      setCached(key, data)
      inflight.delete(key)
      return data
    })
    .catch(err => {
      inflight.delete(key)
      throw err
    })

  inflight.set(key, promise)
  return promise
}

export const geoService = {
  countries: async (): Promise<GeoItem[]> => {
    return fetchOnce(cacheKey('/geo/countries'), async () => {
      const { data } = await api.get('/geo/countries')
      return data.data as GeoItem[]
    })
  },

  states: async (countryKey: string): Promise<GeoItem[]> => {
    const params = { country: countryKey }
    return fetchOnce(cacheKey('/geo/states', params), async () => {
      const { data } = await api.get('/geo/states', { params })
      return data.data as GeoItem[]
    })
  },

  cities: async (countryKey: string, stateKey: string): Promise<GeoItem[]> => {
    const params = { country: countryKey, state: stateKey }
    return fetchOnce(cacheKey('/geo/cities', params), async () => {
      const { data } = await api.get('/geo/cities', { params })
      return data.data as GeoItem[]
    })
  },

  searchCities: async (query: string, countryKey?: string): Promise<GeoItem[]> => {
    const params: Record<string, string> = { q: query }
    if (countryKey) params.country = countryKey
    const { data } = await api.get('/geo/cities/search', { params })
    return data.data
  }
}
